import { Card } from 'external/react';
import Icon from 'external/fortawesome';
import useAccounting from 'context/accounting-context/hook';

const formatAmount = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'usd' }).format(amount / 100);

const formatDate = (unixTimestamp: number) =>
  new Date(unixTimestamp * 1000).toLocaleDateString('es-PR', { year: 'numeric', month: 'long', day: 'numeric' });

const statusLabels: Record<string, { label: string; className: string }> = {
  paid:       { label: 'Pagado',      className: 'bg-success' },
  failed:     { label: 'Fallido',     className: 'bg-danger'  },
  pending:    { label: 'Pendiente',   className: 'bg-warning' },
  in_transit: { label: 'En tránsito', className: 'bg-info'    },
  canceled:   { label: 'Cancelado',   className: 'bg-secondary'},
};

const txnTypeLabels: Record<string, string> = {
  charge:         'Cargos',
  refund:         'Reembolsos',
  adjustment:     'Ajustes',
  stripe_fee:     'Comisiones Stripe',
  payout:         'Pago',
  payout_failure: 'Fallo de pago',
};

const thStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#6b7280',
  borderBottom: '1px solid #e5e7eb',
  padding: '8px 12px',
  background: 'transparent',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid #f3f4f6',
  fontSize: '0.875rem',
};

const tfootTdStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontWeight: 600,
  fontSize: '0.875rem',
  borderTop: '2px solid #e5e7eb',
};

const sectionTitle: React.CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  marginBottom: '12px',
  color: '#111827',
};

const formatFee = (fee: number) => fee === 0 ? '$0.00' : formatAmount(-fee);

const PayoutDetailPage = () => {
  const { payout } = useAccounting();

  if (payout.loading) return (
    <Card.Body className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
      <Icon name="faSpinner" spin size="2x" className="text-muted" />
    </Card.Body>
  );

  if (!payout.data) return null;

  const { payout: payoutRow, summary, transactions } = payout.data;
  const p = payoutRow.properties;

  const summaryRows = ['charge', 'refund', 'adjustment', 'stripe_fee'].map(type => ({
    type,
    label: txnTypeLabels[type] ?? type,
    ...(summary[type] ?? { count: 0, gross: 0, fee: 0, net: 0 })
  }));

  const visibleTransactions = transactions.filter(
    (txn: any) => (txn._type ?? txn.type) !== 'payout'
  );

  return (
    <Card.Body>
      {/* Top section */}
      <div className="row g-4 mb-4">

        {/* Left: Summary */}
        <div className="col-12 col-md-8">
          <p style={sectionTitle}>Resumen</p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}></th>
                <th style={{...thStyle, textAlign: 'right'}}>Cantidad</th>
                <th style={{...thStyle, textAlign: 'right'}}>Bruto</th>
                <th style={{...thStyle, textAlign: 'right'}}>Comisiones</th>
                <th style={{...thStyle, textAlign: 'right'}}>Total</th>
              </tr>
            </thead>
            <tbody>
              {summaryRows.map(row => (
                <tr key={row.type}>
                  <td style={tdStyle}>{row.label}</td>
                  <td style={{...tdStyle, textAlign: 'right'}}>{row.count}</td>
                  <td style={{...tdStyle, textAlign: 'right'}}>{formatAmount(row.gross)}</td>
                  <td style={{...tdStyle, textAlign: 'right'}}>{formatFee(row.fee)}</td>
                  <td style={{...tdStyle, textAlign: 'right'}}>{formatAmount(row.net)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} style={{...tfootTdStyle, textAlign: 'right'}}>Pagos</td>
                <td style={{...tfootTdStyle, textAlign: 'right'}}>{formatAmount(p.amount)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Right: Details */}
        <div className="col-12 col-md-4">
          <p style={sectionTitle}>Detalles</p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {[
                ['Completado', formatDate(p.arrival_date)],
                ['ID',         payoutRow.id],
                ['Método',     p.method],
                ['Trace ID',   p.trace_id?.value ?? '—'],
                ['Comisión',   '$0.00'],
                ['Monto',      formatAmount(p.amount)],
              ].map(([label, value]) => (
                <tr key={label}>
                  <td style={{...tdStyle, color: '#6b7280', width: '40%'}}>{label}</td>
                  <td style={{...tdStyle, wordBreak: 'break-all'}}>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p style={{...sectionTitle, marginTop: '20px'}}>Cuenta</p>
          <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.875rem' }}>
            <Icon name="faCreditCard" className="text-muted" />
            <span>
              {payoutRow.bank_name ? `${payoutRow.bank_name} •••• ${payoutRow.last4}` : '—'}
            </span>
          </div>
        </div>
      </div>

      {/* Bottom: Transactions full width */}
      <p style={sectionTitle}>Transacciones</p>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={thStyle}>Tipo</th>
            <th style={{...thStyle, textAlign: 'right'}}>Bruto</th>
            <th style={{...thStyle, textAlign: 'right'}}>Comisión</th>
            <th style={{...thStyle, textAlign: 'right'}}>Total</th>
            <th style={thStyle}>Descripción</th>
            <th style={thStyle}>Fecha</th>
          </tr>
        </thead>
        <tbody>
          {visibleTransactions.map((txn: any) => (
            <tr key={txn.id}>
              <td style={tdStyle}>{txnTypeLabels[txn._type ?? txn.type] ?? txn.type}</td>
              <td style={{...tdStyle, textAlign: 'right'}}>{formatAmount(txn.amount)}</td>
              <td style={{...tdStyle, textAlign: 'right'}}>{formatFee(txn.fee)}</td>
              <td style={{...tdStyle, textAlign: 'right'}}>{formatAmount(txn.net)}</td>
              <td style={tdStyle}>{txn._description ?? txn.description ?? '—'}</td>
              <td style={tdStyle}>{formatDate(txn.created)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card.Body>
  );
};

export { PayoutDetailPage as default };